import { useRef, useEffect, useState } from 'react'
import {
  createSnippet, createSnippetToken, deleteSnippet, deleteSnippetToken, fetchSnippets,
  patchSnippet, patchSnippetToken,
  type SnippetRow, type SnippetTokenRow, type TokenSourceMeta,
} from '../lib/api.js'
import { MarkupToolbar, QuoteMarkup } from '../lib/markup.js'
import type { Partner } from '@shared/types'

/**
 * Snippets & tokens on their own page (decision 111; editing round 113) —
 * moved off Admin → Organisation so the library is where the people who USE
 * it can reach it.
 *
 * Decision 113: every snippet row starts COLLAPSED (title + one-line
 * preview); expanding shows the rendered text with Edit / Clone / Retire.
 * Editing gets the same B / I / U / list toolbar as the send panel; Clone
 * copies into a library you can write to (a Wave9 snippet cloned by a
 * non-admin lands in their own library — "take it and tweak it").
 *
 * Permissions are unchanged from decisions 67/106: partner people manage
 * their partner's snippets; Wave9-global snippets and the token table are
 * admin-managed (the API refuses anyone else, the UI hides what you can't do).
 */
export function Snippets({ partner, role }: { partner: Partner; role: 'admin' | 'user' | null }) {
  const [snippets, setSnippets] = useState<SnippetRow[]>([])
  const [tokens, setTokens] = useState<SnippetTokenRow[]>([])
  const [tokenSources, setTokenSources] = useState<TokenSourceMeta[]>([])
  const [editable, setEditable] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [openIds, setOpenIds] = useState<Set<string>>(new Set())
  const [editingId, setEditingId] = useState<string | null>(null)

  const [newToken, setNewToken] = useState('')
  const [newTokenSource, setNewTokenSource] = useState('')

  const load = () => {
    fetchSnippets().then((r) => {
      setSnippets(r.snippets)
      setTokens(r.tokens ?? [])
      setTokenSources(r.tokenSources ?? [])
      setEditable(Boolean(r.editable))
      setError(null)
    }).catch((e: Error) => setError(e.message)).finally(() => setLoading(false))
  }
  useEffect(load, [partner.id])

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try { await fn(); load() } catch (e) { setError((e as Error).message) }
  }

  if (loading) return <p className="muted">Loading snippets…</p>

  const admin = role === 'admin'
  const globals = snippets.filter((s) => s.partnerId === null)
  const own = snippets.filter((s) => s.partnerId !== null)

  const toggleOpen = (id: string) => setOpenIds((cur) => {
    const next = new Set(cur)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  // Decision 113: clone lands in a library the person can WRITE to — a Wave9
  // snippet cloned by a non-admin becomes their own editable copy.
  const clone = (s: SnippetRow) => {
    const title = window.prompt('Name for the copy…', `${s.title} (copy)`)
    if (!title?.trim()) return
    const target = s.partnerId === null ? (admin ? null : partner.id) : s.partnerId
    void run(() => createSnippet({ title: title.trim(), body: s.body, partnerId: target }))
  }

  const mayEdit = (s: SnippetRow) => (s.partnerId === null ? admin : editable)

  const snippetRow = (s: SnippetRow) => {
    const open = openIds.has(s.id)
    const editing = editingId === s.id
    const firstLine = s.body.split('\n').find((l) => l.trim()) ?? ''
    return (
      <div key={s.id} className="school" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <div role="button" tabIndex={0} style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}
          title={open ? 'Collapse' : 'Expand'}
          onClick={() => { if (!editing) toggleOpen(s.id) }}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !editing) toggleOpen(s.id) }}>
          <span className="muted" aria-hidden>{open ? '▾' : '▸'}</span>
          <strong style={{ flex: open ? 1 : undefined }}>{s.title}</strong>
          {!open && (
            <span className="small muted" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {firstLine}
            </span>
          )}
        </div>
        {open && !editing && (
          <>
            <div className="qmarkup small" style={{ margin: '8px 0 4px 21px', background: '#f8fafb', borderRadius: 8, padding: '8px 12px' }}>
              <QuoteMarkup text={s.body} />
            </div>
            <div className="rowflex" style={{ marginLeft: 21 }}>
              {mayEdit(s) && <button className="btn btn--sm" onClick={() => setEditingId(s.id)}>Edit</button>}
              {(editable || admin) && <button className="btn btn--sm" onClick={() => clone(s)}>Clone…</button>}
              {mayEdit(s) && <button className="btn btn--sm btn--danger" onClick={() => run(() => deleteSnippet(s.id))}>Retire</button>}
            </div>
          </>
        )}
        {open && editing && (
          <SnippetEditor
            initialTitle={s.title} initialBody={s.body} saveLabel="Save changes"
            onCancel={() => setEditingId(null)}
            onSave={(title, body) => run(async () => {
              await patchSnippet(s.id, { title, body })
              setEditingId(null)
            })}
          />
        )}
      </div>
    )
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Snippets &amp; tokens</h1>
          <p>
            Reusable cover-note and footer text, inserted from the send panel — always editable
            after insert, never locked. Tokens like #FirstName fill in from the quote's own data
            the moment a snippet is inserted.
          </p>
        </div>
      </div>

      {error && <div className="banner banner--stop"><strong>That didn't save.</strong><span>{error}</span></div>}

      <div className="card">
        <div className="card__head">
          <h2>Wave9 library — everyone sees these</h2>
          <span className="muted small">{admin ? 'Managed here (admin) — anyone can clone one into their own library.' : 'Managed by Wave9 admin — clone one to make your own editable copy.'}</span>
        </div>
        <div className="card__body">
          {globals.length === 0 && <p className="muted small">No Wave9-global snippets yet.</p>}
          {globals.map(snippetRow)}
          {admin && <SnippetComposer label="Add a Wave9-global snippet" onAdd={(title, body) => run(() => createSnippet({ title, body, partnerId: null }))} />}
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <h2>{partner.name} — your own library</h2>
          <span className="muted small">Only {partner.name} sees these. "Save as snippet" in the send panel adds here too.</span>
        </div>
        <div className="card__body">
          {own.length === 0 && <p className="muted small">Nothing yet — add one below, or save one from the send panel.</p>}
          {own.map(snippetRow)}
          {editable && <SnippetComposer label="Add a snippet" onAdd={(title, body) => run(() => createSnippet({ title, body, partnerId: partner.id }))} />}
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <h2>Snippet tokens</h2>
          <span className="muted small">
            Placeholders inside snippets — #FirstName, #EndDate, #ARR — filled from the quote's own data
            at insert time. Case doesn't matter; a token with no data stays visible for a human to fill.
            {!admin && ' Managed by Wave9 admin — shown here so you know what works.'}
          </span>
        </div>
        <div className="card__body">
          {tokens.map((t) => (
            <div key={t.id} className="school">
              <strong style={{ width: 160 }}>{t.token}</strong>
              {admin ? (
                <select className="select" style={{ flex: 1, maxWidth: 320 }} value={t.source}
                  onChange={(e) => run(() => patchSnippetToken(t.id, { source: e.target.value }))}>
                  {tokenSources.map((src) => <option key={src.key} value={src.key}>{src.label}</option>)}
                </select>
              ) : (
                <span className="small" style={{ flex: 1, maxWidth: 320 }}>{tokenSources.find((src) => src.key === t.source)?.label ?? t.source}</span>
              )}
              <span className="tiny muted" style={{ flex: 1 }}>{tokenSources.find((src) => src.key === t.source)?.description ?? ''}</span>
              {admin && (
                <button className="btn btn--sm btn--danger"
                  onClick={() => run(() => deleteSnippetToken(t.id))}>Remove</button>
              )}
            </div>
          ))}
          {admin && (
            <div className="rowflex" style={{ marginTop: 10 }}>
              <input className="input" style={{ width: 180 }} placeholder="#NewToken" value={newToken} onChange={(e) => setNewToken(e.target.value)} />
              <select className="select" style={{ width: 260 }} value={newTokenSource} onChange={(e) => setNewTokenSource(e.target.value)}>
                <option value="">Data source…</option>
                {tokenSources.map((src) => <option key={src.key} value={src.key}>{src.label}</option>)}
              </select>
              <button className="btn" disabled={!newToken.trim() || !newTokenSource} onClick={() => run(async () => {
                await createSnippetToken(newToken.trim(), newTokenSource)
                setNewToken(''); setNewTokenSource('')
              })}>Add token</button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/** Title + body editor with the send panel's formatting toolbar (113). */
function SnippetEditor({ initialTitle, initialBody, saveLabel, onSave, onCancel }: {
  initialTitle: string
  initialBody: string
  saveLabel: string
  onSave: (title: string, body: string) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(initialTitle)
  const [body, setBody] = useState(initialBody)
  const ref = useRef<HTMLTextAreaElement | null>(null)
  return (
    <div style={{ margin: '8px 0 2px 21px' }}>
      <div className="rowflex" style={{ marginBottom: 6 }}>
        <input className="input" style={{ width: 260 }} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
        <MarkupToolbar target={() => ref.current} onApply={setBody} />
      </div>
      <textarea ref={ref} className="input" rows={4} style={{ width: '100%' }} value={body}
        onChange={(e) => setBody(e.target.value)} placeholder="Snippet text — customer-facing" />
      <div className="rowflex" style={{ marginTop: 6 }}>
        <button className="btn btn--sm btn--primary" disabled={!title.trim() || !body.trim()}
          onClick={() => onSave(title.trim(), body.trim())}>{saveLabel}</button>
        <button className="btn btn--sm" onClick={onCancel}>Cancel</button>
        <span className="tiny muted">Formatting and #tokens render when the snippet is used on a quote.</span>
      </div>
    </div>
  )
}

/** Collapsed "add" affordance — expands into the same editor (113). */
function SnippetComposer({ label, onAdd }: { label: string; onAdd: (title: string, body: string) => void }) {
  const [open, setOpen] = useState(false)
  if (!open) {
    return (
      <div style={{ marginTop: 10 }}>
        <button className="btn btn--sm" onClick={() => setOpen(true)}>+ {label}</button>
      </div>
    )
  }
  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--line-soft)', paddingTop: 8 }}>
      <SnippetEditor initialTitle="" initialBody="" saveLabel="Add snippet"
        onSave={(t, b) => { onAdd(t, b); setOpen(false) }}
        onCancel={() => setOpen(false)} />
    </div>
  )
}
